import { useState } from 'react';
import { X, HelpCircle, RefreshCw, Calculator } from 'lucide-react';
import { LeaderboardPlayer, DebugPlayerRecord } from '../modules/stats/types';

interface DebugModalProps {
  isOpen: boolean;
  onClose: () => void;
  player: LeaderboardPlayer | null;
  records: DebugPlayerRecord[];
  onRefresh: () => void;
}

export default function DebugModal({ isOpen, onClose, player, records, onRefresh }: DebugModalProps) {
  const [showCalcLog, setShowCalcLog] = useState(false);

  if (!isOpen || !player) return null;

  // V104: 计算过程日志
  const calcLog = records.map((record, idx) => {
    const score = Number(record.score_change) || 0;
    const isWin = score > 0;
    const runningWins = records.slice(0, idx + 1).filter(r => Number(r.score_change) > 0).length;
    const runningTotal = records.slice(0, idx + 1).reduce((sum, r) => sum + (Number(r.score_change) || 0), 0);
    return {
      ...record,
      idx: idx + 1,
      isWin,
      runningWins,
      runningTotal,
    };
  });

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[70] p-4">
      <div className="bg-gradient-to-br from-[#2A1F3D] to-[#1E1A30] rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto border border-blue-500/30 shadow-2xl shadow-blue-500/20">
        <div className="p-4 border-b border-blue-500/20 flex items-center justify-between">
          <h2 className="text-lg font-bold text-white flex items-center gap-2">
            <HelpCircle className="w-5 h-5 text-blue-400" />
            调试详情 - {player.player_name} (#{player.player_number})
          </h2>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-white/10 text-avalon-text transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-4 space-y-4">
          <div className="grid grid-cols-3 gap-3">
            <div className="p-3 rounded-lg bg-blue-500/10 border border-blue-500/20">
              <div className="text-xs text-blue-300">积分榜局数</div>
              <div className="text-xl font-bold text-white">{player.total_games}</div>
            </div>
            <div className="p-3 rounded-lg bg-green-500/10 border border-green-500/20">
              <div className="text-xs text-green-300">实际记录数</div>
              <div className="text-xl font-bold text-white">{records.length}</div>
            </div>
            <div className="p-3 rounded-lg bg-yellow-500/10 border border-yellow-500/20">
              <div className="text-xs text-yellow-300">差异</div>
              <div className={`text-xl font-bold ${player.total_games === records.length ? 'text-green-400' : 'text-red-400'}`}>
                {player.total_games - records.length}
              </div>
            </div>
          </div>

          {/* V104: 计算过程日志开关 */}
          <div className="flex items-center justify-between">
            <button
              onClick={() => setShowCalcLog(!showCalcLog)}
              className="flex items-center gap-2 px-3 py-2 rounded-lg bg-avalon-card text-avalon-text hover:text-white transition-colors"
            >
              <Calculator className="w-4 h-4" />
              <span>{showCalcLog ? '隐藏计算日志' : '显示计算日志'}</span>
            </button>
            <div className="text-xs text-avalon-text">
              总分: <span className="text-white font-bold">{player.total_score}</span> | 
              胜率: <span className="text-white font-bold">{player.win_rate}%</span>
            </div>
          </div>

          {/* V104: 计算过程日志 */}
          {showCalcLog && (
            <div className="bg-avalon-card/30 rounded-lg p-3 max-h-48 overflow-y-auto">
              <div className="text-xs text-avalon-text mb-2">计算过程（逐局累加）：</div>
              <div className="space-y-1">
                {calcLog.map((log) => (
                  <div key={log.idx} className="flex items-center gap-2 text-xs">
                    <span className="text-avalon-text w-6">#{log.idx}</span>
                    <span className="text-white w-20">{log.game_date}</span>
                    <span className="text-avalon-text w-12">第{log.game_number}局</span>
                    <span className={log.isWin ? 'text-green-400' : 'text-red-400'}>
                      {log.isWin ? '✓ 胜' : '✗ 负'}
                    </span>
                    <span className={log.score_change >= 0 ? 'text-green-400' : 'text-red-400'}>
                      {log.score_change > 0 ? '+' : ''}{log.score_change}
                    </span>
                    <span className="text-avalon-text">→</span>
                    <span className="text-blue-400">
                      累计 {log.runningWins} 胜 / {log.idx} 局 / {log.runningTotal} 分
                    </span>
                  </div>
                ))}
              </div>
              <div className="mt-2 pt-2 border-t border-white/10 text-xs">
                <span className="text-avalon-text">最终结果：</span>
                <span className="text-white font-bold">
                  {calcLog.length} 局 · {calcLog.filter(l => l.isWin).length} 胜 · {calcLog.reduce((s, l) => s + Number(l.score_change), 0)} 分
                </span>
              </div>
            </div>
          )}

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-white/10">
                  <th className="text-left p-2 text-avalon-text">日期</th>
                  <th className="text-left p-2 text-avalon-text">局数</th>
                  <th className="text-left p-2 text-avalon-text">角色</th>
                  <th className="text-left p-2 text-avalon-text">阵营</th>
                  <th className="text-left p-2 text-avalon-text">结果</th>
                  <th className="text-right p-2 text-avalon-text">得分</th>
                  <th className="text-center p-2 text-avalon-text">MVP</th>
                </tr>
              </thead>
              <tbody>
                {records.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="text-center p-4 text-avalon-text">
                      未找到有效记录
                    </td>
                  </tr>
                ) : (
                  records.map((record, idx) => (
                    <tr key={idx} className="border-b border-white/5 hover:bg-white/5">
                      <td className="p-2 text-white">{record.game_date}</td>
                      <td className="p-2 text-white">第{record.game_number}局</td>
                      <td className="p-2 text-avalon-text">{record.player_role}</td>
                      <td className="p-2">
                        <span className={`px-2 py-0.5 rounded text-xs ${
                          record.faction === 'good' ? 'bg-blue-500/20 text-blue-300' : 'bg-red-500/20 text-red-300'
                        }`}>
                          {record.faction === 'good' ? '正义' : '邪恶'}
                        </span>
                      </td>
                      <td className="p-2">
                        <span className={`text-xs ${record.is_win ? 'text-green-400' : 'text-red-400'}`}>
                          {record.is_win ? '✓ 胜' : '✗ 负'}
                        </span>
                      </td>
                      <td className="p-2 text-right">
                        <span className={record.score_change >= 0 ? 'text-green-400' : 'text-red-400'}>
                          {record.score_change > 0 ? '+' : ''}{record.score_change}
                        </span>
                      </td>
                      <td className="p-2 text-center">
                        {record.is_mvp && <span className="text-yellow-400">★</span>}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="flex-1 py-3 rounded-lg bg-avalon-card text-white font-medium hover:bg-avalon-purple/50 transition-colors"
            >
              关闭
            </button>
            <button
              onClick={onRefresh}
              className="flex-1 py-3 rounded-lg bg-blue-500 text-white font-medium hover:bg-blue-600 transition-colors"
            >
              <RefreshCw className="w-4 h-4 inline mr-2" />
              刷新数据
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
